// Iter82 — Real-time admin top-bar alert badge for new orders.
//
// Polls /api/admin/orders/alerts/recent every 30 seconds. Tracks the
// last-seen timestamp in localStorage so the red badge only counts
// orders the admin has NOT yet opened. Click → routes to /admin/orders
// and clears the cursor.
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { BellRing } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LS_KEY = "lifted_admin_order_alerts_cursor_v1";

function money(c) {
  if (c == null) return "—";
  return `$${(c / 100).toFixed(2)}`;
}

export default function AdminOrderAlertsBadge() {
  const { user } = useAuth();
  const isAdmin = !!user && (user.roles || []).some((r) => r === "admin" || r === "master_admin");

  const [alerts, setAlerts] = useState([]);
  const [open, setOpen]     = useState(false);
  const [since, setSince]   = useState(() => {
    try { return localStorage.getItem(LS_KEY) || ""; } catch (_) { return ""; }
  });

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders/alerts/recent", {
        params: since ? { since, limit: 20 } : { limit: 20 },
      });
      setAlerts(data.alerts || []);
    } catch (_) { /* swallow — keep last good state */ }
  }, [since]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    fetchAlerts();
    const id = setInterval(fetchAlerts, 30000);
    return () => clearInterval(id);
  }, [isAdmin, fetchAlerts]);

  if (!isAdmin) return null;

  const count = alerts.length;
  const badge = count > 99 ? "99+" : String(count);

  function markSeen() {
    if (alerts.length > 0) {
      const newest = alerts[0]?.created_at || new Date().toISOString();
      try { localStorage.setItem(LS_KEY, newest); } catch (_) { /* storage unavailable */ }
      setSince(newest);
      setAlerts([]);
    }
    setOpen(false);
  }

  return (
    <div className="relative" data-testid="admin-order-alerts">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-md border border-white/10 bg-black/40 text-lifted-green hover:border-lifted-green/60"
        data-testid="admin-order-alerts-toggle"
        title={count ? `${count} new order alert${count === 1 ? "" : "s"}` : "No new orders"}
      >
        <BellRing className="h-4 w-4" />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="admin-order-alerts-count"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-w-[90vw] glass-panel border border-white/10 rounded-md shadow-xl z-50 overflow-hidden"
          data-testid="admin-order-alerts-panel"
        >
          <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.22em] text-lifted-green font-bebas">
              New Orders
            </div>
            <button
              onClick={markSeen}
              className="text-[0.65rem] text-lifted-dim hover:text-white"
              data-testid="admin-order-alerts-mark-seen"
            >
              Mark all seen
            </button>
          </div>
          {alerts.length === 0 ? (
            <div className="p-6 text-center text-xs text-lifted-dim" data-testid="admin-order-alerts-empty">
              No new orders since you last checked.
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-white/5">
              {alerts.map((a) => (
                <li key={a.order_id} className="px-3 py-2 hover:bg-white/5"
                    data-testid={`admin-order-alerts-row-${a.order_id}`}>
                  <Link
                    to={`/admin/orders?focus=${a.order_id}`}
                    onClick={markSeen}
                    className="block"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm text-white truncate">{a.customer}</div>
                      <div className="text-sm text-lifted-green font-mono">{money(a.total_cents)}</div>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[0.65rem] text-lifted-dim">
                      <span className="font-mono">{a.order_number || a.order_id.slice(0, 8)}</span>
                      <span>{(a.created_at || "").slice(11, 16)} UTC</span>
                    </div>
                    {a.vendors && a.vendors.length > 0 && (
                      <div className="mt-1 text-[0.6rem] text-purple-300">
                        {a.vendors.join(" · ")} · {a.item_count} item{a.item_count === 1 ? "" : "s"}
                      </div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="px-3 py-2 border-t border-white/5 text-right">
            <Link
              to="/admin/orders"
              onClick={markSeen}
              className="text-[0.65rem] text-lifted-green hover:underline"
              data-testid="admin-order-alerts-view-all"
            >
              View all orders →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
